"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import {
  fadeUp,
  staggerContainer,
  staggerItem,
  viewportOnce
} from "@/lib/motion";
import { PillBadge } from "@/components/ui/PillBadge";
import { PlaceholderImage } from "@/components/ui/PlaceholderImage";
import { SectionReveal } from "@/components/ui/SectionReveal";
import { GradientDivider } from "@/components/ui/GradientDivider";
import { AccentColor } from "@/lib/colors";
import { ChevronDownIcon } from "@/components/ui/Icons/ChevronDownIcon";

type Category = "All" | "Comparisons" | "Guides" | "Roundups";

interface Article {
  slug: string;
  title: string;
  description: string;
  readTime: string;
  category: Category;
  categoryColor: string;
  author: string;
  date: string;
}

const articles: Article[] = [
  {
    slug: "best-scaffolding-software",
    title: "6 Best Scaffolding Software Tools: The Complete Comparison",
    description:
      "The best scaffolding software compared: Scaffcalc, LayPLAN Classic, Avontus, Scaffmax, NTI Scaffplanner, and ScaffPlan. See which tool fits your workflow.",
    readTime: "9 min",
    category: "Roundups",
    categoryColor: "var(--color-bubblegum)",
    author: "Kevin Walian",
    date: "2026-04-13"
  },
  {
    slug: "scaffcalc-vs-excel",
    title: "Scaffcalc vs Excel: Why Spreadsheets Cost You 15% of Your Margin",
    description:
      "Most scaffold contractors still quote in Excel. We break down the hidden costs: from formula errors to version chaos: and show why purpose-built software wins every time.",
    readTime: "9 min",
    category: "Comparisons",
    categoryColor: "var(--color-bubblegum)",
    author: "Scaffcalc Team",
    date: "2026-03-10"
  },
  {
    slug: "scaffcalc-vs-avontus",
    title: "Scaffcalc vs Avontus: Which Scaffolding Software Actually Works",
    description:
      "Avontus has been around for years, but does legacy mean better? We compare pricing, usability, and real contractor feedback side-by-side.",
    readTime: "6 min",
    category: "Comparisons",
    categoryColor: "var(--color-lavender)",
    author: "Scaffcalc Team",
    date: "2026-03-03"
  },
  {
    slug: "scaffcalc-vs-scaffplan",
    title:
      "Scaffcalc vs ScaffPlan: What Actually Matters for Your Scaffold Quoting",
    description:
      "ScaffPlan focuses on 3D planning. But what about quoting speed, material tracking, and real-world usability? Here is the honest breakdown.",
    readTime: "8 min",
    category: "Comparisons",
    categoryColor: "var(--color-bubblegum)",
    author: "Scaffcalc Team",
    date: "2026-02-24"
  },
  {
    slug: "scaffcalc-vs-smartscaffolder",
    title: "Scaffcalc vs SmartScaffolder: A Contractor's Honest Comparison",
    description:
      "SmartScaffolder promises automation. But how does it perform on real jobs? We asked contractors who have used both tools to share their experience.",
    readTime: "8 min",
    category: "Comparisons",
    categoryColor: "var(--color-lavender)",
    author: "Scaffcalc Team",
    date: "2026-02-17"
  },
  {
    slug: "scaffcalc-vs-cad",
    title: "Scaffcalc vs CAD Software: Why Contractors Are Ditching AutoCAD",
    description:
      "AutoCAD is powerful: but it was never built for scaffolding. We explain why contractors are switching to specialized tools that speak their language.",
    readTime: "12 min",
    category: "Guides",
    categoryColor: "var(--color-aegean)",
    author: "Scaffcalc Team",
    date: "2026-02-10"
  }
];

const categories: Category[] = ["All", "Roundups", "Comparisons", "Guides"];

export function BlogListingContent() {
  const t = useTranslations("Blog");
  const [activeCategory, setActiveCategory] = useState<Category>("All");
  const [sortNewest, setSortNewest] = useState(true);

  const filteredArticles = articles
    .filter((a) => activeCategory === "All" || a.category === activeCategory)
    .sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortNewest ? dateB - dateA : dateA - dateB;
    });

  return (
    <>
      {/* ── Hero ── */}
      <section className="pt-6 lg:pt-32 pb-10 lg:pb-16 px-6">
        <motion.div
          className="mx-auto max-w-3xl text-center"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <PillBadge label={t("heroPill")} variant={AccentColor.Bubblegum} />
          <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight text-text-primary mt-6">
            {t("heroTitle")}
          </h1>
          <p className="mt-6 text-text-body max-w-xl mx-auto">
            {t("heroSubtitle")}
          </p>
        </motion.div>
      </section>

      {/* ── Gradient Divider ── */}
      <GradientDivider variant={AccentColor.Bubblegum} />

      {/* ── Filter Bar ── */}
      <section className="py-8 px-6">
        <div className="mx-auto max-w-4xl flex flex-wrap items-center justify-between gap-4">
          {/* Category pills */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`font-body text-xs font-extrabold uppercase tracking-widest px-5 py-2.5 rounded-full transition-transform duration-150 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bubblegum ${
                  activeCategory === cat
                    ? "bg-bubblegum text-text-primary"
                    : "bg-bg-surface border border-border text-text-muted hover:border-text-muted hover:text-text-primary"
                }`}
              >
                {cat === "All"
                  ? t("filterAll")
                  : cat === "Comparisons"
                    ? t("filterComparisons")
                    : t("filterGuides")}
              </button>
            ))}
          </div>

          {/* Sort dropdown */}
          <button
            onClick={() => setSortNewest(!sortNewest)}
            className="font-body text-xs font-bold text-text-muted hover:text-text-primary active:scale-95 transition-transform duration-150 flex items-center gap-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lavender"
          >
            {t("sortLabel")}{" "}
            <span className="text-text-primary">
              {sortNewest ? t("sortNewest") : t("sortOldest")}
            </span>
            <ChevronDownIcon
              className={`w-4 h-4 transition-transform duration-150 ${sortNewest ? "" : "rotate-180"}`}
            />
          </button>
        </div>
      </section>

      {/* ── Article List ── */}
      <section className="pb-12 lg:pb-20 px-6">
        <motion.div
          className="mx-auto max-w-4xl"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          {filteredArticles.map((article, i) => (
            <motion.div key={article.slug} variants={staggerItem}>
              <a
                href={`/resources/news/${article.slug}`}
                className="group flex flex-col sm:flex-row gap-6 py-8 transition-transform duration-150 hover:translate-x-1 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-bubblegum rounded-lg"
              >
                {/* Thumbnail */}
                <div className="flex-shrink-0">
                  <PlaceholderImage
                    height="h-[110px]"
                    label={article.title}
                    className="w-[180px] rounded-lg border border-border"
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Category + read time */}
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className="font-body text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full"
                      style={{
                        color: article.categoryColor,
                        backgroundColor: `${article.categoryColor}15`,
                        border: `1px solid ${article.categoryColor}30`
                      }}
                    >
                      {article.category}
                    </span>
                    <span className="text-text-muted text-xs font-body">
                      {article.readTime} {t("readSuffix")}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="font-display text-xl font-bold tracking-tight text-text-primary group-hover:text-bubblegum transition-colors duration-150">
                    {article.title}
                  </h2>

                  {/* Description */}
                  <p className="mt-2 text-text-body text-sm line-clamp-2">
                    {article.description}
                  </p>

                  {/* Author + date */}
                  <div className="mt-3 flex items-center gap-2 text-text-muted text-xs font-body">
                    <span>{article.author}</span>
                    <span className="text-border">|</span>
                    <span>{article.date}</span>
                  </div>
                </div>
              </a>

              {/* Divider between cards */}
              {i < filteredArticles.length - 1 && (
                <div className="h-px bg-border" />
              )}
            </motion.div>
          ))}

          {/* Empty state */}
          {filteredArticles.length === 0 && (
            <div className="text-center py-16">
              <p className="text-text-muted font-body text-sm">
                {t("emptyState")}
              </p>
            </div>
          )}
        </motion.div>
      </section>
    </>
  );
}
